import { debug } from '../utils/debug';
import { useEffect } from 'react';

/**
 * Custom hook to dynamically update document title and favicon
 * based on merchant data
 *
 * @param {Object} merchantData - Merchant configuration object
 * @param {string} merchantData.config.display_name - Merchant display name
 * @param {string} merchantData.config.description - Merchant description
 * @param {string} merchantData.config.favicon_url - URL to favicon image
 */
export function useDocumentMeta(merchantData) {
  useEffect(() => {
    if (!merchantData?.config) return;

    const { display_name, description, favicon_url } = merchantData.config;

    // Update document title
    let title;
    if (display_name) {
      // If display_name exists, use display_name or display_name - description
      title = description
        ? `${display_name} - ${description}`
        : display_name;
    } else if (description) {
      // If no display_name, use just description
      title = description;
    }

    if (title) {
      document.title = title;

      // Update meta tags
      const metaTags = {
        'og:title': title,
        'twitter:title': title,
      };

      // Only add author if display_name exists
      if (display_name) {
        metaTags['author'] = display_name;
      }

      Object.entries(metaTags).forEach(([name, content]) => {
        // Check for property attribute (og:title)
        let meta = document.querySelector(`meta[property="${name}"]`);
        if (!meta) {
          // Check for name attribute (twitter:title, author)
          meta = document.querySelector(`meta[name="${name}"]`);
        }

        if (meta) {
          meta.setAttribute('content', content);
        }
      });

      // Update JSON-LD structured data
      const jsonLdScript = document.querySelector('script[type="application/ld+json"]');
      if (jsonLdScript) {
        try {
          const jsonLd = JSON.parse(jsonLdScript.textContent);
          if (display_name) {
            jsonLd.name = display_name;
          }
          if (description) {
            jsonLd.description = description;
          }
          jsonLdScript.textContent = JSON.stringify(jsonLd, null, 2);
        } catch (e) {
          debug.warn('Failed to update JSON-LD:', e);
        }
      }
    }

    // Update favicon
    if (favicon_url) {
      // Remove existing favicon links
      const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
      existingFavicons.forEach(link => link.remove());

      // Add new favicon
      const link = document.createElement('link');
      link.rel = 'icon';

      // Determine type from URL extension
      const extension = favicon_url.split('.').pop().toLowerCase();
      const typeMap = {
        'svg': 'image/svg+xml',
        'png': 'image/png',
        'ico': 'image/x-icon',
        'jpg': 'image/jpeg',
        'jpeg': 'image/jpeg',
      };
      link.type = typeMap[extension] || 'image/x-icon';
      link.href = favicon_url;

      document.head.appendChild(link);
    }

    // Cleanup function to restore defaults if needed
    return () => {
      // Optionally restore default title on unmount
      // document.title = 'Bike Rental Platform';
    };
  }, [merchantData]);
}
